// Shared constants/components for the Radiology Service Management screens —
// same role as lab-technician/labOrdersShared.js, kept as its own module
// because radiology has its own status/priority vocabulary and no
// sample/barcode concept.
import React from "react";

// "Reported" is the terminal state once the radiologist has finalized the
// report (see RadiologyReportModal.js) — that's what "Ready" means here now.
export const READY_STATUSES = ["Reported"];

export const STATUS_OPTIONS = [
  "Ordered",
  "Scheduled",
  "Acknowledged",
  "Started",
  "In Progress",
  "Completed",
  "Cancelled",
  "Reported",
];

export const MODALITY_OPTIONS = [
  "CT",
  "MRI",
  "X-Ray",
  "Ultrasound",
  "Mammography",
  "PET",
  "Fluoroscopy",
];

export const PRIORITY_OPTIONS = ["Routine", "Urgent", "STAT"];

// Modality names reach us free-hand — the service master says "CT Scan", another
// feed says "COMPUTED TOMOGRAPHY", a third says "USG" — but a template can only
// ever be authored against MODALITY_OPTIONS. Everything that compares two
// modalities (order↔template, dashboard filter) runs both sides through here
// first; without it a "CT Scan" order can never find its "CT" template.
//
// PET is tested before CT on purpose: "PET-CT" is a PET study, and \bct\b would
// otherwise claim it.
export const matchModality = (value) => {
  const s = String(value || "").trim().toLowerCase();
  if (!s || s === "-") return "";
  if (/\bpet\b/.test(s)) return "PET";
  if (/\bct\b|computed tomography/.test(s)) return "CT";
  if (/\bmri\b|magnetic resonance/.test(s)) return "MRI";
  if (/x-?ray|radiograph/.test(s)) return "X-Ray";
  if (/ultrasound|\busg\b|sonograph|doppler/.test(s)) return "Ultrasound";
  if (/mammograph/.test(s)) return "Mammography";
  if (/fluoroscop/.test(s)) return "Fluoroscopy";
  return "";
};

// As above, but an unrecognised modality survives as typed rather than being
// blanked — a hospital using a modality we don't know about must still be able
// to match its own orders to its own templates.
export const canonicalModality = (value) =>
  matchModality(value) || String(value || "").trim();

export const STATUS_META = {
  Ordered: { bg: "#F3F4F6", color: "#374151" },
  Scheduled: { bg: "#ECFEFF", color: "#0E7490" },
  Acknowledged: { bg: "#EEF2FF", color: "#4338CA" },
  Started: { bg: "#FEFCE8", color: "#A16207" },
  "In Progress": { bg: "#FFF7ED", color: "#C2410C" },
  Completed: { bg: "#ECFDF5", color: "#15803D" },
  Cancelled: { bg: "#FEF2F2", color: "#B91C1C" },
  Reported: { bg: "#F0FDFA", color: "#0F766E" },
};

export const PRIORITY_META = {
  STAT: { bg: "#FEF2F2", color: "#B91C1C" },
  Urgent: { bg: "#FFFBEB", color: "#B45309" },
  Routine: { bg: "#F3F4F6", color: "#374151" },
};

const istParts = (d, options) => {
  const map = {};
  new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", ...options })
    .formatToParts(d)
    .forEach((p) => {
      if (p.type !== "literal") map[p.type] = p.value;
    });
  return map;
};

export const formatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = istParts(d, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  const period = (p.dayPeriod || "").toUpperCase();
  return `${p.day}/${p.month}/${p.year}, ${p.hour}:${p.minute}:${p.second} ${period}`.trim();
};

// Referring doctors reach us as bare names ("Tiya Sarakr"). Every place that
// shows one — dashboard column, report modal, printed report — reads better
// with a title, and the guard keeps an API that already sends "Dr." from
// stuttering into "Dr. Dr.".
export const withSalutation = (name) => {
  const value = String(name || "").trim();
  if (!value || value === "-") return "-";
  return /^(dr|doctor|prof|mr|mrs|ms)\b\.?/i.test(value) ? value : `Dr. ${value}`;
};

export const formatDateOnly = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const p = istParts(d, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  return `${p.day}/${p.month}/${p.year}`;
};

// Newest placement first, everywhere an order list is shown — a study ordered
// just now must land at the top of the dashboard, not wherever the API happened
// to return it. Order number breaks ties within the same timestamp (RAD-…-014
// above RAD-…-013), and unparseable timestamps sink rather than shuffle.
export const orderedAtMs = (order) => {
  const t = Date.parse(order?.ordered);
  return Number.isNaN(t) ? 0 : t;
};

export const sortOrdersNewestFirst = (orders) =>
  [...(orders || [])].sort(
    (a, b) =>
      orderedAtMs(b) - orderedAtMs(a) ||
      String(b.orderNo || "").localeCompare(String(a.orderNo || "")),
  );

// One doctor save (one add_patient_lab_tests POST) = one batch — same
// grouping key convention as labOrdersShared.resolveOrderBatchKey, kept
// independent here so a change to the lab grouping rules doesn't silently
// change radiology's.
export const resolveOrderBatchKey = (order) => {
  if (order?.batchId) return String(order.batchId);
  const ts = String(order?.ordered || "").slice(0, 19);
  const doctor = String(order?.doctor || "-").trim();
  if (ts) return `batch-${ts}-${doctor}`;
  return `__single_${order?.orderNo || "unknown"}`;
};

// Explodes an admission's orders into one group per order-placement batch —
// no sample-type split (radiology has no sample concept), unlike Lab's
// groupOrdersByBatch. Used to give each doctor save its own top-level
// "patient" row instead of folding every study into one row.
export function groupOrdersByBatchOnly(orders) {
  const groups = [];
  const byBatchKey = new Map();

  (orders || []).forEach((order) => {
    const batchKey = resolveOrderBatchKey(order);
    let group = byBatchKey.get(batchKey);
    if (!group) {
      group = { batchKey, orders: [] };
      byBatchKey.set(batchKey, group);
      groups.push(group);
    }
    group.orders.push(order);
  });

  groups.forEach((group) => {
    group.primaryOrderNo = group.orders[0]?.orderNo || "-";
    group.latestOrdered = group.orders.reduce(
      (latest, o) => (!latest || o.ordered > latest ? o.ordered : latest),
      null,
    );
  });

  // Newest batch first — re-ordering for a patient already on the dashboard
  // has to surface above that patient's earlier placements, not under them.
  return groups.sort((a, b) =>
    String(b.latestOrdered || "").localeCompare(String(a.latestOrdered || "")),
  );
}

// Studies sharing a modality collapse into a single detail row: the modality is
// stated once and each study stacks inside the row's other cells. Insertion
// order is preserved, so a newest-first order list yields newest-first groups.
export function groupOrdersByModality(orders) {
  const groups = [];
  const byModality = new Map();

  (orders || []).forEach((order) => {
    const key = order?.modality || "-";
    let group = byModality.get(key);
    if (!group) {
      group = { modality: key, orders: [] };
      byModality.set(key, group);
      groups.push(group);
    }
    group.orders.push(order);
  });

  return groups;
}

export function StatusPill({ status }) {
  const m = STATUS_META[status] || { bg: "#F3F4F6", color: "#374151" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        background: m.bg,
        color: m.color,
        whiteSpace: "nowrap",
      }}
    >
      {status}
    </span>
  );
}

export function PriorityPill({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.Routine;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 700,
        background: m.bg,
        color: m.color,
        whiteSpace: "nowrap",
      }}
    >
      {priority}
    </span>
  );
}

// ── Unified get_ipd_radiology_tests/ (paginated All/OPD/IPD board) ─────────────

const DEFAULT_ORDER_STATUS = "Ordered";
const DEFAULT_PRIORITY = "Routine";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** "07/09/2026, 07:00:00 AM" (UTC) → ISO with Z; otherwise returns the input string. */
export const parseOrderedTime = (str) => {
  if (!str) return null;
  const m =
    /^(\d{2})\/(\d{2})\/(\d{4}),?\s*(\d{2}):(\d{2}):(\d{2})\s*(AM|PM)$/i.exec(
      String(str).trim(),
    );
  if (!m) return str;
  const [, mm, dd, yyyy, hh12, min, ss, ampm] = m;
  let hh = parseInt(hh12, 10) % 12;
  if (/pm/i.test(ampm)) hh += 12;
  const pad = (n) => String(n).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${pad(hh)}:${min}:${ss}Z`;
};

export const normalizePriorityLabel = (priority) => {
  if (!priority) return DEFAULT_PRIORITY;
  const lower = String(priority).trim().toLowerCase();
  if (lower === "stat") return "STAT";
  if (lower === "urgent") return "Urgent";
  if (lower === "routine") return "Routine";
  const raw = String(priority).trim();
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

/**
 * Map backend status into radiology STATUS_OPTIONS. Placement values (NEW/PAID)
 * collapse to Ordered. OPD COMPLETED → Completed. Unknown workflow values are
 * kept as-is so a dashboard write (Started, Acknowledged, …) is not painted
 * back as Ordered on the next fetch.
 */
export const normalizeRadiologyOrderStatus = (status) => {
  const raw = String(status || "").trim();
  if (!raw) return DEFAULT_ORDER_STATUS;
  const upper = raw.toUpperCase();
  if (upper === "NEW" || upper === "PAID") return DEFAULT_ORDER_STATUS;
  if (upper === "COMPLETED") return "Completed";
  if (upper === "IN PROGRESS" || upper === "IN_PROGRESS") return "In Progress";
  if (upper === "SAMPLE ACKNOWLEDGED") return "Acknowledged";
  const known = STATUS_OPTIONS.find(
    (s) => s.toLowerCase() === raw.toLowerCase(),
  );
  return known || raw;
};

export const resolveModality = (service, modalityName) => {
  if (modalityName) return canonicalModality(modalityName);
  return matchModality(service) || "-";
};

/** Prefer UUID-shaped medical_service_id for template APIs. */
export const resolveMedicalServiceId = (o) => {
  const uuid = [
    o?.medical_service_uuid,
    o?.medical_service_id,
    o?.service_id,
    o?.service_uuid,
  ].find((v) => UUID_RE.test(String(v ?? "")));
  return uuid ? String(uuid) : String(o?.medical_service_id ?? "");
};

/** Nested / flat order line from get_ipd_radiology_tests/. */
export const mapRadiologyApiOrder = (o, fallbackDoctor = "") => {
  const service = o.service_name || o.service || o.test_name || "—";
  // OPD `tests[]` often has no order_no — synthesize a stable key so the row
  // still renders (IPD empty order_no lines stay dropped unless a fallback is
  // provided by the caller).
  const orderNo = String(
    o.order_no ??
      o.orderNo ??
      o._synthetic_order_no ??
      "",
  ).trim();
  if (!orderNo) return null;
  return {
    orderNo,
    batchId: o.lab_test_batch_id || o.batch_id || undefined,
    service,
    modality: resolveModality(service, o.modality_name),
    cpt: o.medical_service_no ?? o.medical_service_id ?? "-",
    priority: normalizePriorityLabel(o.priority),
    doctor: o.doctor_name || fallbackDoctor || "-",
    department:
      o.service_classification_name || o.department || o.dept || "",
    ordered: parseOrderedTime(o.ordered_time || o.ordered),
    scheduled: o.scheduled_time ? parseOrderedTime(o.scheduled_time) : null,
    status: normalizeRadiologyOrderStatus(o.status),
    medicalServiceId: resolveMedicalServiceId(o),
    medicalServiceNo: o.medical_service_no ?? o.medical_service_id ?? null,
    sampleRadiologyId: o.sample_radiology_id ?? null,
    modalityCreatedAt: o.modality_created_at || null,
    modalityUpdatedAt: o.modality_updated_at || null,
    serviceClassificationId: o.service_classification_id ?? null,
    labTestId: o.lab_test_id || o.radiology_test_id || null,
    radiologyTestId: o.radiology_test_id || o.lab_test_id || null,
  };
};

/**
 * Flat OPD radiology_test_list row with no nested orders[] —
 * synthesize one board order from top-level fields.
 */
export function mapFlatOpdRadiologyEntryToOrder(item) {
  const testId = item.radiology_test_id || item.lab_test_id || "";
  const orderNo = String(
    item.order_no ||
      (Array.isArray(item.orders) && item.orders[0]?.order_no) ||
      testId ||
      "",
  );
  const service = item.test_name || item.service_name || "—";
  return mapRadiologyApiOrder({
    order_no: orderNo,
    service_name: service,
    medical_service_id: item.medical_service_id,
    medical_service_uuid: item.medical_service_uuid,
    medical_service_no: item.medical_service_no,
    priority: item.priority,
    doctor_name: item.doctor_name || item.doctor_name_unregistered,
    ordered_time: item.ordered_time || item.appointment_start_time,
    scheduled_time: item.scheduled_time,
    modality_name: item.modality_name,
    status: item.status,
    lab_test_id: item.lab_test_id,
    radiology_test_id: item.radiology_test_id || item.lab_test_id,
    lab_test_batch_id: item.lab_test_batch_id || testId,
    service_classification_name: item.service_classification_name,
    service_classification_id: item.service_classification_id,
    sample_radiology_id: item.sample_radiology_id,
  });
}

// An order placed from the emergency department carries patient_type
// "EMERGENCY". It used to be folded into OPD here, which made an ED order
// indistinguishable from a walk-in — and OPD assumptions (a billed
// appointment, no bed) do not hold for one. Blank still falls back to the
// admission check: the column was added after those rows started being written.
const resolvePatientType = (item) => {
  const raw = item?.patient_type;
  if (raw == null || raw === "") {
    if (item?.admission_id != null && item.admission_id !== "") return "IPD";
    return "OPD";
  }
  const value = String(raw).toUpperCase();
  if (value === "IPD") return "IPD";
  if (value === "EMERGENCY") return "EMERGENCY";
  return "OPD";
};

const isMissingDemoValue = (value) =>
  value === undefined || value === null || value === "" || value === "-";

const radiologyStatusProgressRank = (status) => {
  const idx = STATUS_OPTIONS.indexOf(status);
  return idx === -1 ? 0 : idx;
};

const preferOrderCopy = (a, b) => {
  if (!a) return b;
  if (!b) return a;
  const rankA = radiologyStatusProgressRank(a.status);
  const rankB = radiologyStatusProgressRank(b.status);
  if (rankB !== rankA) return rankB > rankA ? b : a;
  const scoreOne = (o) => {
    let s = 0;
    if (o.modality && o.modality !== "-") s += 30;
    if (o.ordered) s += 20;
    if (o.cpt && o.cpt !== "-") s += 10;
    if (o.doctor && o.doctor !== "-") s += 5;
    if (o.medicalServiceNo != null) s += 5;
    if (o.scheduled) s += 5;
    return s;
  };
  return scoreOne(b) - scoreOne(a) > 0 ? b : a;
};

export const dedupeOrdersByOrderNo = (orders) => {
  const best = new Map();
  (orders || []).forEach((order) => {
    const key = String(order?.orderNo || "").trim();
    if (!key) return;
    best.set(key, preferOrderCopy(best.get(key), order));
  });
  return Array.from(best.values()).sort((a, b) =>
    String(a.orderNo).localeCompare(String(b.orderNo)),
  );
};

const mergePatientDemographics = (target, source) => {
  if (!source) return target;
  const fields = [
    "name",
    "uhid",
    "bedNo",
    "roomNo",
    "age",
    "gender",
    "dob",
    "phone",
    "email",
    "abhaId",
    "doctorName",
    "appointmentMode",
    "appointmentStartTime",
    "patientId",
    "careContextId",
    "appointmentId",
  ];
  fields.forEach((key) => {
    if (isMissingDemoValue(target[key]) && !isMissingDemoValue(source[key])) {
      target[key] = source[key];
    }
  });
  if (!target.labTestId && source.labTestId) {
    target.labTestId = source.labTestId;
  }
  if (!target.radiologyTestId && source.radiologyTestId) {
    target.radiologyTestId = source.radiologyTestId;
  }
  return target;
};

const mapRadiologyTestListEntry = (item) => {
  const patientType = resolvePatientType(item);
  const radiologyTestId = String(
    item.radiology_test_id || item.lab_test_id || "",
  ).trim();
  const admissionId = item.admission_id ?? null;
  const appointmentId = item.appointment_id || null;
  const patientId = item.patient_id || "";
  // OPD rows are keyed by appointment (tests[] has no radiology_test_id).
  const batchKey =
    radiologyTestId ||
    appointmentId ||
    item.care_context_id ||
    patientId ||
    "row";

  const id =
    patientType === "IPD" && admissionId != null && admissionId !== ""
      ? String(admissionId)
      : `${patientId || "opd"}::${batchKey}`;

  const fallbackDoctor =
    item.doctor_name || item.doctor_name_unregistered || "";
  // IPD uses orders[]; OPD from get_ipd_radiology_tests/ uses tests[].
  const nestedOrders = Array.isArray(item.orders)
    ? item.orders
    : Array.isArray(item.tests)
      ? item.tests
      : null;
  let orders;
  if (nestedOrders && nestedOrders.length > 0) {
    orders = nestedOrders
      .map((o, idx) => {
        const synthetic =
          String(o.order_no || o.orderNo || "").trim() ||
          [
            patientType === "OPD" ? "OPD" : "RAD",
            appointmentId || admissionId || patientId || "x",
            o.medical_service_no || o.medical_service_id || "svc",
            idx,
          ].join("-");
        return mapRadiologyApiOrder(
          {
            ...o,
            order_no: o.order_no || o.orderNo || undefined,
            _synthetic_order_no: synthetic,
            doctor_name: o.doctor_name || fallbackDoctor,
            lab_test_id: o.lab_test_id || radiologyTestId || undefined,
            radiology_test_id:
              o.radiology_test_id || radiologyTestId || undefined,
            lab_test_batch_id:
              o.lab_test_batch_id ||
              o.batch_id ||
              appointmentId ||
              undefined,
          },
          fallbackDoctor,
        );
      })
      .filter(Boolean);
  } else {
    const flat = mapFlatOpdRadiologyEntryToOrder(item);
    orders = flat ? [flat] : [];
  }

  return {
    id: String(id),
    admissionId: patientType === "IPD" ? admissionId : null,
    patientId,
    labTestId: radiologyTestId || null,
    radiologyTestId: radiologyTestId || null,
    appointmentId,
    careContextId: item.care_context_id || null,
    patientType,
    name: item.patient_name || "Patient",
    uhid: item.uhid || "-",
    bedNo: item.bed_no || item.bedNo || "-",
    roomNo: item.room_no || item.roomNo || "-",
    age: item.age ?? item.age_years ?? null,
    gender: item.gender || "-",
    dob: item.dob || "-",
    email: item.email || "",
    phone: item.patient_phone_no || item.phone_no || item.phone || "-",
    abhaId: item.abha_id || null,
    doctorName: fallbackDoctor || "-",
    appointmentMode: item.appointment_mode || "",
    appointmentStartTime: item.appointment_start_time || "",
    orders: sortOrdersNewestFirst(orders),
  };
};

/**
 * Convert get_ipd_radiology_tests/ list into board patient rows.
 * IPD entries that share an admission_id collapse into one patient;
 * OPD stays one row per radiology_test_id / lab_test_id.
 */
export function transformUnifiedRadiologyTestsToPatients(testList) {
  if (!Array.isArray(testList)) return [];

  const mapped = testList
    .filter((item) => item && typeof item === "object")
    .map(mapRadiologyTestListEntry)
    .filter((row) => row.orders.length > 0);

  const opdRows = [];
  const ipdByAdmission = new Map();

  mapped.forEach((row) => {
    const isIpd =
      row.patientType === "IPD" &&
      row.admissionId != null &&
      row.admissionId !== "";
    if (!isIpd) {
      opdRows.push(row);
      return;
    }

    const key = String(row.admissionId);
    const existing = ipdByAdmission.get(key);
    if (!existing) {
      ipdByAdmission.set(key, { ...row, id: key, orders: [...row.orders] });
      return;
    }
    mergePatientDemographics(existing, row);
    existing.orders = existing.orders.concat(row.orders);
  });

  const ipdRows = Array.from(ipdByAdmission.values()).map((patient) => ({
    ...patient,
    orders: sortOrdersNewestFirst(dedupeOrdersByOrderNo(patient.orders)),
  }));

  const normalizedOpd = opdRows.map((patient) => ({
    ...patient,
    orders: sortOrdersNewestFirst(dedupeOrdersByOrderNo(patient.orders)),
  }));

  return [...ipdRows, ...normalizedOpd];
}

/** Pull radiology_test_list (or fallbacks) out of the API envelope. */
export function extractPatientRadiologyTestsFromResponse(data) {
  if (!data) return [];
  if (Array.isArray(data.radiology_test_list)) return data.radiology_test_list;
  if (Array.isArray(data.lab_test_list)) return data.lab_test_list;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data)) return data;
  return [];
}

/**
 * Build query params for GET get_ipd_radiology_tests/.
 * patientType "ALL" (or empty) omits patient_type so every kind is returned.
 */
export function buildRadiologyTestsQueryParams({
  page = 1,
  pageSize = 25,
  patientType,
  search,
  status,
  priority,
  dateFrom,
  dateTo,
  orderedTime,
  admissionId,
  appointmentId,
  labTestId,
  radiologyTestId,
  orderNo,
  medicalServiceId,
} = {}) {
  const params = {};
  const pageNum = Number(page);
  const sizeNum = Number(pageSize);
  if (Number.isFinite(pageNum) && pageNum > 0) params.page = pageNum;
  if (Number.isFinite(sizeNum) && sizeNum > 0) params.page_size = sizeNum;

  const type = String(patientType || "").trim().toUpperCase();
  if (type === "IPD" || type === "OPD" || type === "EMERGENCY") {
    params.patient_type = type;
  }

  const q = String(search || "").trim();
  if (q) params.search = q;

  if (status && status !== "All statuses") params.status = status;

  if (priority && priority !== "All priorities") {
    params.priority = String(priority).trim().toLowerCase();
  }

  if (dateFrom) params.ordered_time_from = dateFrom;
  if (dateTo) params.ordered_time_to = dateTo;
  if (orderedTime) params.ordered_time = orderedTime;

  if (admissionId != null && admissionId !== "") {
    params.admission_id = String(admissionId);
  }
  if (appointmentId) params.appointment_id = String(appointmentId);
  const testId = radiologyTestId || labTestId;
  if (testId) {
    params.radiology_test_id = String(testId);
    params.lab_test_id = String(testId);
  }
  if (orderNo) params.order_no = String(orderNo);
  if (medicalServiceId) params.medical_service_id = String(medicalServiceId);

  return params;
}

export function extractRadiologyTestsPagination(
  data,
  fallbackPage = 1,
  fallbackSize = 25,
) {
  const page =
    Number(data?.page ?? data?.current_page ?? fallbackPage) || fallbackPage;
  const pageSize =
    Number(data?.page_size ?? data?.pageSize ?? fallbackSize) || fallbackSize;
  const totalCountRaw =
    data?.total_count ?? data?.totalCount ?? data?.count ?? data?.total;
  const totalCount =
    totalCountRaw == null || totalCountRaw === ""
      ? null
      : Number(totalCountRaw);
  return {
    page,
    pageSize,
    totalCount: Number.isFinite(totalCount) ? totalCount : null,
  };
}

/**
 * GET get_ipd_radiology_tests/ with auth headers.
 * Returns { patients, pagination, raw, list }.
 */
export async function fetchRadiologyTestsPage(
  axios,
  authHeaders,
  query = {},
  config = {},
) {
  const params = buildRadiologyTestsQueryParams(query);
  const res = await axios.get(
    `${process.env.REACT_APP_BACKEND_ROUTE_URL}get_ipd_radiology_tests/`,
    { headers: authHeaders, params, ...config },
  );
  if (res.data?.is_valid === false) {
    throw new Error(res.data?.message || "Request rejected by server");
  }
  const list = Array.isArray(res.data?.radiology_test_list)
    ? res.data.radiology_test_list
    : extractPatientRadiologyTestsFromResponse(res.data);
  const patients = transformUnifiedRadiologyTestsToPatients(list);
  const pagination = extractRadiologyTestsPagination(
    res.data,
    params.page || 1,
    params.page_size || 25,
  );
  return { patients, pagination, raw: res.data, list };
}
